/**
 * Idiomas en los que puede estar escrita una trivia.
 *
 * Igual que `countries.ts`, vive en `shared/` para que el desplegable del
 * editor y la validación del servidor miren la misma lista.
 *
 * El código es ISO 639-1, que es lo que se guarda. Sirve además como valor del
 * atributo `lang` si algún día se marca el idioma en el HTML de la partida,
 * cosa que los lectores de pantalla agradecen.
 */

export interface Language {
  code: string;
  name: string;
}

export const LANGUAGES: readonly Language[] = [
  { code: 'es', name: 'Español' },
  { code: 'en', name: 'Inglés' },
  { code: 'pt', name: 'Portugués' },
  { code: 'fr', name: 'Francés' },
  { code: 'de', name: 'Alemán' },
  { code: 'it', name: 'Italiano' },
  { code: 'ca', name: 'Catalán' },
  { code: 'eu', name: 'Euskera' },
  { code: 'gl', name: 'Gallego' },
  { code: 'nl', name: 'Neerlandés' },
  { code: 'pl', name: 'Polaco' },
  { code: 'ro', name: 'Rumano' },
  { code: 'ru', name: 'Ruso' },
  { code: 'uk', name: 'Ucraniano' },
  { code: 'tr', name: 'Turco' },
  { code: 'ar', name: 'Árabe' },
  { code: 'he', name: 'Hebreo' },
  { code: 'hi', name: 'Hindi' },
  { code: 'zh', name: 'Chino' },
  { code: 'ja', name: 'Japonés' },
  { code: 'ko', name: 'Coreano' },
  { code: 'id', name: 'Indonesio' },
  { code: 'sw', name: 'Suajili' },
  { code: 'el', name: 'Griego' },
  { code: 'sv', name: 'Sueco' },
  { code: 'no', name: 'Noruego' },
  { code: 'da', name: 'Danés' },
  { code: 'fi', name: 'Finés' },
  { code: 'cs', name: 'Checo' },
  { code: 'hu', name: 'Húngaro' },
  { code: 'vi', name: 'Vietnamita' },
  { code: 'th', name: 'Tailandés' },
];

/** El de la interfaz. Es el idioma de casi todo lo que se crea aquí. */
export const DEFAULT_LANGUAGE = 'es';

const BY_CODE = new Map(LANGUAGES.map((l) => [l.code, l]));

export function isLanguageCode(value: unknown): value is string {
  return typeof value === 'string' && BY_CODE.has(value.toLowerCase());
}

/** Normaliza a un código conocido; lo desconocido cae al idioma por defecto. */
export function normalizeLanguage(value: unknown): string {
  const code = String(value ?? '').toLowerCase().slice(0, 2);
  return BY_CODE.has(code) ? code : DEFAULT_LANGUAGE;
}

export function languageName(code: string | null | undefined): string | null {
  if (!code) return null;
  return BY_CODE.get(code.toLowerCase())?.name ?? null;
}
